/**
 * Cliente HTTP contra la FastAPI del repo.
 *
 * Responsabilidades, y ninguna mas:
 *
 * 1. Adjuntar el JWT de Supabase como `Authorization: Bearer <token>`. El
 *    backend (`src/api/deps.py::enforce_authentication`) exige ese header en
 *    todo endpoint que no este en la lista blanca publica.
 * 2. Traducir los codigos de la API a errores tipados que la UI pueda
 *    distinguir, en particular **401 (sesion) vs 403 (tenencia)**: son dos
 *    cosas distintas y colapsarlas en "algo fallo" hace imposible reaccionar
 *    bien (a un 401 se lo desloguea; a un 403 no, la sesion esta perfecta y lo
 *    que falta es acceso a ESA tienda).
 * 3. Nunca reintentar en silencio. Un panel que reintenta esconde el problema.
 *
 * El token se pide con un callback (`getToken`) y no se guarda: Supabase rota
 * el access token cada hora y lo refresca solo. Cachearlo aca garantizaria
 * mandar tokens vencidos justo despues de cada refresh.
 */

import { API_V1_PREFIX } from "../config";

/** Error de una respuesta HTTP no exitosa. */
export class ApiError extends Error {
  readonly status: number;
  readonly path: string;
  /** `detail` del cuerpo, si la respuesta traia JSON de FastAPI. */
  readonly detail: string | undefined;

  constructor(status: number, path: string, detail?: string) {
    super(detail ? `HTTP ${status} en ${path}: ${detail}` : `HTTP ${status} en ${path}`);
    this.name = "ApiError";
    this.status = status;
    this.path = path;
    this.detail = detail;
  }

  /** Sesion invalida o vencida: hay que volver a autenticar. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Token valido, pero sin acceso a la tienda pedida (tenencia). */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** El deploy no tiene Supabase Auth configurado (backend responde 503). */
  get isAuthNotConfigured(): boolean {
    return this.status === 503;
  }
}

/** Error de red: la request no llego a completarse. */
export class NetworkError extends Error {
  readonly path: string;

  constructor(path: string, cause: unknown) {
    super(`No se pudo contactar la API (${path})`);
    this.name = "NetworkError";
    this.path = path;
    this.cause = cause;
  }
}

/** Devuelve el access token vigente, o `null` si no hay sesion. */
export type TokenProvider = () => Promise<string | null>;

export interface RequestOptions {
  /** Query params. Los `undefined` se omiten. */
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  /** Para cancelar desde un `useEffect` que se desmonta. */
  readonly signal?: AbortSignal;
}

/**
 * Extraer el `detail` de un cuerpo de error de FastAPI.
 *
 * FastAPI usa `detail` como string en los `HTTPException` y como lista de
 * objetos en los errores de validacion (422). Se cubren los dos, y cualquier
 * otra cosa se ignora: un mensaje de error mal parseado no puede romper el
 * manejo del error.
 */
async function readErrorDetail(response: Response): Promise<string | undefined> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return undefined;
  }

  if (typeof body !== "object" || body === null || !("detail" in body)) {
    return undefined;
  }

  const detail = (body as { detail: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) =>
        typeof item === "object" && item !== null && "msg" in item
          ? String((item as { msg: unknown }).msg)
          : null,
      )
      .filter((msg): msg is string => msg !== null);
    return messages.length > 0 ? messages.join("; ") : undefined;
  }
  return undefined;
}

function buildUrl(
  baseUrl: string,
  path: string,
  query: RequestOptions["query"],
): string {
  const url = `${baseUrl}${API_V1_PREFIX}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const serialized = params.toString();
  return serialized ? `${url}?${serialized}` : url;
}

export class ApiClient {
  readonly #baseUrl: string;
  readonly #getToken: TokenProvider;

  /**
   * @param baseUrl Base de la API sin barra final. Cadena vacia = mismo origen.
   * @param getToken Callback que devuelve el access token vigente.
   */
  constructor(baseUrl: string, getToken: TokenProvider) {
    this.#baseUrl = baseUrl;
    this.#getToken = getToken;
  }

  /**
   * GET tipado. El tipo `T` viene de `src/api/types.ts`, o sea del OpenAPI.
   *
   * Ojo: `T` es una afirmacion sobre lo que el backend devuelve, no una
   * validacion en runtime. Es la contrapartida honesta de generar los tipos
   * desde el contrato: si el backend miente, TypeScript no lo atrapa. Lo que si
   * garantiza el generador es que el contrato y el cliente no se separen.
   *
   * @throws {ApiError} Si la respuesta no es 2xx.
   * @throws {NetworkError} Si la request no llega a completarse.
   */
  async get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const token = await this.#getToken();
    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const url = buildUrl(this.#baseUrl, path, options.query);

    let response: Response;
    try {
      response = await fetch(url, {
        method: "GET",
        headers,
        // Sin cookies: la credencial es el header Authorization. Mandar
        // credenciales ademas obligaria al backend a un CORS con
        // allow_credentials, que src/main.py deshabilita ante un origen
        // wildcard (ver el comentario ahi).
        credentials: "omit",
        ...(options.signal ? { signal: options.signal } : {}),
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
      throw new NetworkError(path, cause);
    }

    if (!response.ok) {
      throw new ApiError(response.status, path, await readErrorDetail(response));
    }

    // 204 No Content: no hay cuerpo que parsear.
    if (response.status === 204) return undefined as T;

    return (await response.json()) as T;
  }
}
